import { computed, inject, Injectable, signal } from '@angular/core';
import { ContentParser } from '@thiememeulenhoff/js-sdk';
import { ContentService } from '@thiememeulenhoff/ng-api-gateway';
import { keyBy } from 'lodash-es';
import {
  Arrangement,
  ArrangementResource,
  ArrangementType,
  ContentEntry,
  Resource,
  ResourceType,
} from '@thiememeulenhoff/types';
import { map, tap } from 'rxjs';
import { flattenResources } from '../../_utils/flatten-resources';

@Injectable({
  providedIn: 'root',
})
export class ManagementContentFacade {
  readonly #contentService = inject(ContentService);

  #contentParser?: ContentParser;

  readonly #resourcesById = signal<
    Record<string, ArrangementResource | Resource>
  >({});

  readonly #contentById = signal<Record<string, ContentEntry>>({});

  readonly #arrangementsByCode = signal<Record<string, Arrangement>>({});

  readonly #arrangementResourceByCode = signal<
    Record<string, ArrangementResource>
  >({});

  readonly #arrangementResourcesById = signal<
    Record<string, ArrangementResource>
  >({});

  readonly resources = this.#resourcesById.asReadonly();

  readonly content = this.#contentById.asReadonly();

  readonly arrangements = this.#arrangementsByCode.asReadonly();

  readonly arrangementResourcesByCode =
    this.#arrangementResourceByCode.asReadonly();

  readonly arrangementResources = this.#arrangementResourcesById.asReadonly();

  readonly arrangement = (code: string) =>
    computed(() => this.arrangements()[code]);

  readonly arrangementsList = computed(() =>
    Object.values(this.arrangements()),
  );

  readonly arrangementResourceByCode = (arrangementCode: string) =>
    computed(() => this.arrangementResourcesByCode()[arrangementCode]);

  readonly arrangementResourceById = (contentIdentifier: string) =>
    computed(() => this.arrangementResources()[contentIdentifier]);

  readonly resourceById = (contentIdentifier: string) =>
    computed(() => this.resources()[contentIdentifier]);

  readonly contentById = (contentIdentifier: string) =>
    computed(() => this.content()[contentIdentifier]);

  enableLocalParsing() {
    this.#contentParser = new ContentParser('assets/content/local-index.xsl');
  }

  fetchArrangementResource(arrangementCode: string) {
    return this.#fetchArrangementResource(arrangementCode, [
      'module',
      'chapter',
      'paragraph',
    ]);
  }

  fetchArrangementResourceWithSources(arrangementCode: string) {
    return this.#fetchArrangementResource(arrangementCode, [
      'module',
      'chapter',
      'paragraph',
      'sourceMap',
      'source',
    ]);
  }

  #fetchArrangementResource(
    arrangementCode: string,
    includeTypes: Array<ResourceType>,
  ) {
    return this.#contentService
      .fetchManagementResourceByArrangementCode<ArrangementResource>(
        arrangementCode,
        '',
        4,
        {
          include: [
            'thedContentIdentifier',
            'alexContentIdentifier',
            'streamMetadata.method',
            'navigationMetadata.audience',
            'navigationMetadata.backgroundImage',
            'navigationMetadata.backgroundImageCopyright',
            'navigationMetadata.hasTerms',
            'navigationMetadata.hasSources',
            'navigationMetadata.hasResultables',
            'navigationMetadata.links',
            'navigationMetadata.contentType',
            'learningMetadata.language',
            'learningMetadata.typicalLearningTime',
            'instructorAssets',
          ],
        },
        {
          include: includeTypes,
        },
      )
      .pipe(tap(this.#upsertArrangementResources));
  }

  fetchResources(
    arrangementCode: string,
    contentIdentifier: string,
    disabled = true,
    types?: { include?: Array<ResourceType>; exclude?: Array<ResourceType> },
  ) {
    return this.#contentService
      .fetchManagementResourceByArrangementCode<Resource>(
        arrangementCode,
        contentIdentifier,
        5,
        {
          include: [
            'thedContentIdentifier',
            'alexContentIdentifier',
            'terms',
            'streamMetadata.method',
            'navigationMetadata.scoreType',
            'navigationMetadata.questionType',
            'navigationMetadata.hasTheory',
            'navigationMetadata.hasTerms',
            'navigationMetadata.hasSources',
            'navigationMetadata.hasResultables',
            'navigationMetadata.backgroundImage',
            'navigationMetadata.backgroundImageCopyright',
            'navigationMetadata.audience',
            'navigationMetadata.links',
            'navigationMetadata.contentType',
            'editionMetadata.glueContent',
            'editionMetadata.glueContentTitle',
            'editionMetadata.assessment',
            'learningMetadata.language',
            'learningMetadata.isAdaptive',
            'learningMetadata.typicalLearningTime',
            'smartMetadata.allLearningGoals',
            'instructorAssets',
          ],
        },
        types,
        disabled,
      )
      .pipe(tap(this.#upsertResources));
  }

  fetchContent(
    arrangementCode: string,
    contentIdentifier: string,
    includeSourceMaterial = false,
  ) {
    const parser = this.#contentParser;
    const fetch = parser
      ? this.#contentService.fetchRawContentById
      : this.#contentService.fetchManagementContentById;

    return fetch(
      arrangementCode,
      contentIdentifier,
      includeSourceMaterial,
    ).pipe(
      map((content) => parser?.parseFromString(content) || content),
      tap((content) => this.#upsertContent(contentIdentifier, content)),
    );
  }

  fetchArrangement(code: string) {
    return this.#contentService
      .fetchManagementArrangementByCode(code)
      .pipe(tap((arrangement) => this.#upsertArrangements([arrangement])));
  }

  fetchArrangementsByType(type: ArrangementType) {
    return this.#contentService
      .fetchManagementPublishedArrangementsOfType(type)
      .pipe(
        tap((arrangements) => this.#upsertArrangements(arrangements, type)),
      );
  }

  #upsertArrangementResources = (resources: Array<ArrangementResource>) => {
    this.#arrangementResourceByCode.update(
      (current) =>
        ({
          ...current,
          ...keyBy(
            resources
              .map(
                (resource) =>
                  ({
                    ...resource,
                    code: resource.streamMetadata?.code,
                  }) as ArrangementResource,
              )
              .filter((resource) => !!resource.code),
            'code',
          ),
        }) as Record<string, ArrangementResource>,
    );
    this.#arrangementResourcesById.update(
      (current) =>
        ({
          ...current,
          ...keyBy(resources, 'contentIdentifier'),
        }) as Record<string, ArrangementResource>,
    );
    this.#resourcesById.update(
      (current) =>
        ({
          ...current,
          ...keyBy(flattenResources(resources), 'contentIdentifier'),
        }) as Record<string, ArrangementResource | Resource>,
    );
  };

  #upsertContent = (contentIdentifier: string, content: string) => {
    this.#contentById.update((current) => ({
      ...current,
      [contentIdentifier]: { contentIdentifier, content },
    }));
  };

  #upsertResources = (resources: Array<Resource>) => {
    this.#resourcesById.update(
      (current) =>
        ({
          ...current,
          ...keyBy(flattenResources(resources), 'contentIdentifier'),
        }) as Record<string, ArrangementResource | Resource>,
    );
  };

  #upsertArrangements = (
    arrangements: Array<Arrangement>,
    type?: ArrangementType,
  ) => {
    this.#arrangementsByCode.update((current) => {
      const next = { ...current };

      if (type) {
        Object.keys(next).forEach((code) => {
          if (next[code]?.type === type) {
            delete next[code];
          }
        });
      }

      return {
        ...next,
        ...keyBy(arrangements, 'code'),
      };
    });
  };
}
